/**
 * The Neusix Sales Console shell: sidebar, mobile top bar, content area.
 *
 * INTERNAL. Only the internal pages render this; the client's Business
 * Discovery page has its own frame and imports nothing from `src/internal/`.
 *
 * Navigation lists only what exists: the dashboard and the Business
 * Discoveries list. A report, transcript or document view belongs to one
 * session and is reached from that session's pages.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import neusixLogo from '../asserts/neusix_logo.png';
import '../styles/Console.css';

const NAV = [
  { to: '/', label: 'Dashboard', icon: 'dashboard', exact: true },
  {
    to: '/internal/discoveries',
    label: 'Business Discoveries',
    icon: 'list',
    prefixes: ['/internal/discoveries', '/internal/sessions/'],
  },
];

function NavIcon({ name }) {
  return (
    <svg viewBox="0 0 24 24" className="nsc-icon" aria-hidden="true" focusable="false">
      {name === 'dashboard' ? (
        <>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </>
      ) : (
        <>
          <path d="M8 6h12M8 12h12M8 18h12" />
          <path d="M4.5 6h.01M4.5 12h.01M4.5 18h.01" />
        </>
      )}
    </svg>
  );
}

function Brand({ compact = false }) {
  return (
    <Link to="/" className={`nsc-brand${compact ? ' nsc-brand--compact' : ''}`}>
      <span className="nsc-brand__mark" aria-hidden="true">NS</span>
      <span className="nsc-brand__text">
        <img src={neusixLogo} alt="Neusix" className="nsc-brand__logo" />
        <span className="nsc-brand__product">Sales Console</span>
      </span>
    </Link>
  );
}

export default function ConsoleLayout({ title, children }) {
  const { pathname } = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    document.title = title ? `${title} · Neusix Sales Console` : 'Neusix Sales Console';
  }, [title]);

  useEffect(() => {
    document.body.classList.add('nsc-body');
    return () => document.body.classList.remove('nsc-body');
  }, []);

  useEffect(() => { setNavOpen(false); }, [pathname]);

  const isActive = (item) =>
    item.exact ? pathname === item.to : item.prefixes.some((prefix) => pathname.startsWith(prefix));

  return (
    <div className={`nsc${navOpen ? ' nsc--nav-open' : ''}`}>
      <a href="#nsc-content" className="nsc-skip">Skip to content</a>

      <aside id="nsc-sidebar" className="nsc-sidebar" aria-label="Sales Console navigation">
        <Brand />
        <nav className="nsc-nav" aria-label="Main">
          <ul className="nsc-nav__list">
            {NAV.map((item) => {
              const active = isActive(item);
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`nsc-nav__link${active ? ' nsc-nav__link--active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <NavIcon name={item.icon} />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="nsc-sidebar__foot">
          {user && (
            <>
              {/* Box 1: User Profile Badge */}
              <div style={{
                padding: '10px 12px',
                background: '#F1F5F9',
                border: '1px solid #CBD5E1',
                borderRadius: '10px',
                fontSize: '13px',
                color: '#0F172A',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '8px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
              }}>
                <div style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 4px rgba(37, 99, 235, 0.25)',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: '#0F172A',
                  fontWeight: '600'
                }}>
                  {user.name || user.username || user.email || 'NS Operator'}
                </div>
              </div>

              {/* Box 2: Sign Out Action Button */}
              <button
                type="button"
                onClick={logout}
                style={{
                  width: '100%',
                  padding: '9px 14px',
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#991B1B',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '14px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 2px rgba(220, 38, 38, 0.05)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#FEE2E2';
                  e.currentTarget.style.borderColor = '#F87171';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#FEF2F2';
                  e.currentTarget.style.borderColor = '#FCA5A5';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Sign Out</span>
              </button>
            </>
          )}
        </div>
      </aside>

      {navOpen && (
        <button
          type="button"
          className="nsc-scrim"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
        />
      )}

      <div className="nsc-main">
        <header className="nsc-topbar">
          <button
            type="button"
            className="nsc-menu"
            aria-controls="nsc-sidebar"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((open) => !open)}
          >
            <svg viewBox="0 0 24 24" className="nsc-icon" aria-hidden="true" focusable="false">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
            <span className="nsc-sr-only">Open navigation</span>
          </button>
          <Brand compact />
        </header>

        <main id="nsc-content" className="nsc-content">
          {children}
        </main>
      </div>
    </div>
  );
}
